import type { RequestOptions } from '@@/plugin-request/request';
import { request as umiRequest } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';

type ScopedRequestOptions = RequestOptions;
type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue> | undefined;
type RequestData = unknown;

export type AppRequestInstance = (<T = unknown>(
  url: string,
  options?: ScopedRequestOptions,
) => Promise<T>) & {
  get<T = unknown>(
    url: string,
    params?: QueryParams,
    options?: ScopedRequestOptions,
  ): Promise<T>;
  post<T = unknown>(
    url: string,
    data?: RequestData,
    headers?: Record<string, string>,
    options?: ScopedRequestOptions,
  ): Promise<T>;
  put<T = unknown>(
    url: string,
    data?: RequestData,
    options?: ScopedRequestOptions,
  ): Promise<T>;
  patch<T = unknown>(
    url: string,
    data?: RequestData,
    options?: ScopedRequestOptions,
  ): Promise<T>;
  delete<T = unknown>(
    url: string,
    data?: RequestData,
    options?: ScopedRequestOptions,
  ): Promise<T>;
};

const normalizeParams = (params?: QueryParams) => {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null,
    ),
  );
};

const toRequestOptions = (input?: RequestData): { data?: RequestData } => {
  if (!input) return {};
  if (input instanceof FormData) return { data: input };
  return { data: input };
};

export const createRequest = (baseURL?: string): AppRequestInstance => {
  const scopedBaseURL = baseURL ?? BizAppConfig.appApiUrl ?? '';
  const request = <T = unknown>(url: string, options?: ScopedRequestOptions) =>
    (
      umiRequest as unknown as <R = unknown>(
        requestUrl: string,
        requestOptions?: ScopedRequestOptions,
      ) => Promise<R>
    )<T>(url, {
      baseURL: scopedBaseURL,
      ...(options || {}),
    });

  return Object.assign(request, {
    get<T = unknown>(
      url: string,
      params?: QueryParams,
      options?: ScopedRequestOptions,
    ) {
      return request<T>(url, {
        method: 'GET',
        params: normalizeParams(params),
        ...(options || {}),
      });
    },

    post<T = unknown>(
      url: string,
      data?: RequestData,
      headers?: Record<string, string>,
      options?: ScopedRequestOptions,
    ) {
      return request<T>(url, {
        method: 'POST',
        ...toRequestOptions(data),
        ...(headers ? { headers } : {}),
        ...(options || {}),
      });
    },

    put<T = unknown>(
      url: string,
      data?: RequestData,
      options?: ScopedRequestOptions,
    ) {
      return request<T>(url, {
        method: 'PUT',
        ...toRequestOptions(data),
        ...(options || {}),
      });
    },

    patch<T = unknown>(
      url: string,
      data?: RequestData,
      options?: ScopedRequestOptions,
    ) {
      return request<T>(url, {
        method: 'PATCH',
        ...toRequestOptions(data),
        ...(options || {}),
      });
    },

    delete<T = unknown>(
      url: string,
      data?: RequestData,
      options?: ScopedRequestOptions,
    ) {
      return request<T>(url, {
        method: 'DELETE',
        ...toRequestOptions(data),
        ...(options || {}),
      });
    },
  });
};

export const createAppRequest = () => createRequest(BizAppConfig.appApiUrl);
